// What sits over a live camera picture: the aiming corners with their sweeping line, the focus
// ring, the dim scrim behind a busy or failed state and the small hint pills. The picture is
// dark whatever the theme, so these carry their own contrast instead of theme colours.
import "./config";
import { View, styled } from "tamagui";
import { Column } from "./layout";
import { Body } from "./text";

const Corner = styled(View, {
  name: "ScanCorner",
  position: "absolute",
  width: 34,
  height: 34,
  borderColor: "accent",
  borderWidth: 0,
  variants: {
    at: {
      tl: { top: 0, left: 0, borderTopWidth: 3, borderLeftWidth: 3, borderTopLeftRadius: 12 },
      tr: { top: 0, right: 0, borderTopWidth: 3, borderRightWidth: 3, borderTopRightRadius: 12 },
      bl: {
        bottom: 0,
        left: 0,
        borderBottomWidth: 3,
        borderLeftWidth: 3,
        borderBottomLeftRadius: 12,
      },
      br: {
        bottom: 0,
        right: 0,
        borderBottomWidth: 3,
        borderRightWidth: 3,
        borderBottomRightRadius: 12,
      },
    },
  } as const,
});

/** The aiming frame: four corners, and while `sweeping` a thin line moving across to show detection is live. */
export function ScanFrame({ sweeping }: { sweeping: boolean }): React.JSX.Element {
  return (
    <View
      position="absolute"
      top={26}
      left={26}
      right={26}
      bottom={26}
      overflow="hidden"
      pointerEvents="none"
      aria-hidden
    >
      <Corner at="tl" />
      <Corner at="tr" />
      <Corner at="bl" />
      <Corner at="br" />
      {sweeping ? (
        <View
          className="ui-scanline"
          position="absolute"
          left="12%"
          right="12%"
          top="50%"
          height={2}
          borderRadius={1}
          backgroundColor="accent"
          opacity={0.8}
        />
      ) : null}
    </View>
  );
}

/** A ring at the point where a tap focused the camera. */
export function FocusRing({ x, y }: { x: number; y: number }): React.JSX.Element {
  return (
    <View
      position="absolute"
      width={56}
      height={56}
      borderRadius="pill"
      borderWidth={2}
      borderColor="#ffffff"
      pointerEvents="none"
      aria-hidden
      data-testid="scan-focus"
      style={{ left: x - 28, top: y - 28, boxShadow: "0 0 0 1px rgba(12, 5, 8, 0.5)" }}
    />
  );
}

/** The dim layer over the picture for a spinner or a message with choices. */
export const CameraScrim = styled(Column, {
  name: "CameraScrim",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(12, 5, 8, 0.72)",
  variants: {
    panel: { true: { gap: "4", paddingVertical: "6", paddingHorizontal: "4" } },
  } as const,
});

const HintPill = styled(View, {
  name: "CameraHint",
  position: "absolute",
  left: "50%",
  paddingHorizontal: "3",
  paddingVertical: 7,
  borderRadius: "pill",
  backgroundColor: "rgba(12, 5, 8, 0.6)",
  transitionProperty: "opacity",
  transitionDuration: "var(--durFast)",
  variants: {
    at: {
      top: { top: 14 },
      bottom: { bottom: 14 },
    },
  } as const,
});

export interface CameraHintProps {
  at: "top" | "bottom";
  /** Mounted but empty is a live region that is announced reliably once filled. */
  children: string;
  role?: "status";
  "aria-live"?: "polite";
}

/** A short line on a pill over the picture; an empty one fades out instead of leaving. */
export function CameraHint({ at, children, ...aria }: CameraHintProps): React.JSX.Element {
  return (
    <HintPill
      at={at}
      opacity={children === "" ? 0 : 1}
      pointerEvents="none"
      style={{ transform: "translateX(-50%)" }}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...aria}
    >
      {children === "" ? null : (
        <Body size="small" weight="strong" color="#ffffff" whiteSpace="nowrap">
          {children}
        </Body>
      )}
    </HintPill>
  );
}
